import { mayRecordConsumption, membershipStatus } from '../utils/membershipPeriod';
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import DrinkListExport from '../components/DrinkListExport';
import DocumentViewer from '../components/DocumentViewer';
import { ModalShell, ModalActions, ModalSection } from '../components/modals/core';
import MemberAvatar from '../components/MemberAvatar';
import AttributeLabel from '../components/AttributeLabel';
import { ToastContainer } from '../components/Toast';
import { StatCard } from '../components/StatCard';
import GuestBadge from '../components/GuestBadge';
import {
  PlusIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  BanknotesIcon,
  CreditCardIcon,
  UserPlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  ClipboardDocumentListIcon,
  TableCellsIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  PaperClipIcon,
  ChevronDownIcon,
  EyeIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

interface Member {
  geloescht?: number;
  austritt_am?: string | null;
  geloescht_am?: string | null;
  mg_nr: number;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  foto_path?: string;
  foto_path_updated?: number;
  is_guest?: boolean;
}

interface Event {
  id: number;
  beschriftung: string;
  title?: string;
  date?: string;
  abrechnungsmonat?: string | null;
  document_id?: number | null;
  document_name?: string | null;
  document_path?: string | null;
}

interface EventDocument {
  id: number;
  name: string;
  file_path: string;
  created_at?: string;
  document_type?: string;
  size?: number;
}

interface DrinkEntry {
  id: number;
  member_id: number;
  month: number; // Event ID
  amount: number;
  paid: number;
  paid_date?: string;
  paid_by?: number;
  payment_method?: 'cash' | 'paypal' | null;
  token?: string | null;
  event_label: string;
}

interface GroupedDrinkData {
  geloescht?: number;
  austritt_am?: string | null;
  geloescht_am?: string | null;
  member_id: number;
  mitglied_nr?: string;
  name: string;
  vorname: string;
  email: string;
  foto_path?: string;
  foto_path_updated?: number;
  is_guest?: boolean;
  roles?: string[];
  role_descriptions?: string[];
  drinks: Array<{
    drink_id: number;
    event_id: number;
    event_label: string;
    amount: number;
    paid: number;
    paid_date?: string;
    payment_method?: string;
    token?: string | null;
  }>;
  totals: {
    total: number;
    paid: number;
    open: number;
  };
}

interface GroupedApiResponse {
  data: GroupedDrinkData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  events: Event[];
}

interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onConfirm?: () => void;
}

/** Betrag in deutschem Format mit Euro-Zeichen. */
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);

/** Ansichtsmodus laut Design-Entwurf, plus die bisherige Matrix. */
type ViewMode = 'members' | 'events' | 'matrix';

type PaymentMethod = 'cash' | 'paypal';

/** Zahlweise der Schnelleingabe: sofort bezahlt (Bar/PayPal) oder offen lassen. */
type QuickEntryMethod = PaymentMethod | 'open';

interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

const VerzehrkostenBeta: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);
  const [members, setMembers] = useState<Member[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [drinkEntries, setDrinkEntries] = useState<DrinkEntry[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedDrinkData[]>([]);
  const [useGroupedApi, setUseGroupedApi] = useState(true);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'open' | 'with_entries' | 'hide_paid_events'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventMonth, setNewEventMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(() => new Date().getFullYear());
  const [showQuickEntryModal, setShowQuickEntryModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [quickEntryEventId, setQuickEntryEventId] = useState<number | null>(null);
  const [editingEntry, setEditingEntry] = useState<{ memberId: number; eventId: number; amount: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set());
  const [showGuests, setShowGuests] = useState(false); // Default: hide guests without entries
  const [documentEvent, setDocumentEvent] = useState<Event | null>(null);
  const [availableDocuments, setAvailableDocuments] = useState<EventDocument[]>([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentSaving, setDocumentSaving] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<EventDocument | null>(null);
  // Ansicht laut Design-Entwurf: Akkordeon nach Mitgliedern bzw. Events,
  // zusätzlich die bisherige Matrix als dritter Modus.
  const [viewMode, setViewMode] = useState<ViewMode>('members');
  const [expandedMembers, setExpandedMembers] = useState<Record<number, boolean>>({});
  const [expandedEvents, setExpandedEvents] = useState<Record<number, boolean>>({});
  // Schnelleingabe erfasst alle Mitglieder auf einmal (Entwurf).
  const [qeAmounts, setQeAmounts] = useState<Record<number, string>>({});
  const [qeMethod, setQeMethod] = useState<QuickEntryMethod>('cash');
  const [qeSaving, setQeSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  /** Betrag aus Freitext lesen; akzeptiert Komma und Punkt. */
  const parseAmount = (value: string | undefined): number => {
    const n = parseFloat(String(value ?? '').replace(',', '.'));
    return isNaN(n) || n < 0 ? 0 : Math.round(n * 100) / 100;
  };

  const addToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    try {
      if (useGroupedApi) {
        // Use optimized grouped API
        const response = await apiClient.get('/api/drinks/grouped', {
          params: {
            page: 1,
            limit: 100,
            status: filter === 'open' ? 'open' : undefined
          }
        });
        
        setGroupedData(response.data.data);
        setEvents(response.data.events);
        
        // Convert grouped data to flat structure for compatibility
        const flatDrinks: DrinkEntry[] = [];
        const membersSet = new Map<number, Member>();
        
        response.data.data.forEach((member: GroupedDrinkData) => {
          // Check if member is a guest based on roles
          const isGuest = member.roles?.includes('guest') || member.is_guest === true;

          membersSet.set(member.member_id, {
            mg_nr: member.member_id,
            mitglied_nr: member.mitglied_nr || member.member_id.toString(),
            name: member.name,
            vorname: member.vorname,
            email: member.email,
            foto_path: member.foto_path,
            foto_path_updated: member.foto_path_updated,
            is_guest: isGuest,
            geloescht: member.geloescht, austritt_am: member.austritt_am, geloescht_am: member.geloescht_am
          });
          
          member.drinks.forEach(drink => {
            flatDrinks.push({
              id: drink.drink_id,
              member_id: member.member_id,
              month: drink.event_id,
              amount: drink.amount,
              paid: drink.paid,
              paid_date: drink.paid_date,
              payment_method: drink.payment_method as 'cash' | 'paypal' | null,
              token: drink.token,
              event_label: drink.event_label
            });
          });
        });
        
        const sortedMembers = Array.from(membersSet.values()).sort((a, b) =>
          a.vorname.localeCompare(b.vorname, 'de')
        );

        setMembers(sortedMembers);
        setDrinkEntries(flatDrinks);
        
        // Select first event by default
        if (response.data.events.length > 0 && !selectedEvent) {
          setSelectedEvent(response.data.events[0]);
        }
      } else {
        // Fallback to old API
        const [membersRes, eventsRes, drinksRes] = await Promise.all([
          apiClient.get('/api/members?includeWithDrinks=1'),
          apiClient.get('/api/events'),
          apiClient.get('/api/drinks')
        ]);

        const sortedMembers = membersRes.data.sort((a: Member, b: Member) =>
          a.vorname.localeCompare(b.vorname, 'de')
        );
        setMembers(sortedMembers);
        const sortedEvents = eventsRes.data.sort((a: Event, b: Event) => b.id - a.id);
        setEvents(sortedEvents);
        setDrinkEntries(drinksRes.data);

        if (eventsRes.data.length > 0 && !selectedEvent) {
          setSelectedEvent(eventsRes.data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to old API if grouped API fails
      if (useGroupedApi) {
        console.log('Grouped API failed, falling back to traditional API');
        setUseGroupedApi(false);
        return fetchData();
      }
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Daten konnten nicht geladen werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      setConfirmModal({
        visible: true,
        type: 'warning',
        title: 'Hinweis',
        message: 'Bitte geben Sie einen Event-Namen ein.',
        variant: 'single'
      });
      return;
    }

    try {
      await apiClient.post('/api/events', {
        beschriftung: newEventName,
        abrechnungsmonat: `${newEventMonth}-01`
      });

      setShowNewEventModal(false);
      setNewEventName('');

      // Reload all data to ensure consistency
      await fetchData();

      setSelectedYear(Number(newEventMonth.slice(0, 4)));

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Event wurde erfolgreich erstellt.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Error creating event:', error);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Event konnte nicht erstellt werden.',
        variant: 'single'
      });
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    // Check if event has entries
    const hasEntries = drinkEntries.some(entry => entry.month === eventId);
    
    if (hasEntries) {
      setConfirmModal({
        visible: true,
        type: 'warning',
        title: 'Nicht möglich',
        message: 'Event kann nicht gelöscht werden, da bereits Einträge vorhanden sind.',
        variant: 'single'
      });
      return;
    }

    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Event löschen',
      message: 'Möchten Sie dieses Event wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        try {
          // Start delete animation
          setDeletingEventId(eventId);
          
          // Wait for animation to complete
          setTimeout(async () => {
            await apiClient.delete(`/api/drinks/events/${eventId}`);
            setEvents(events.filter(e => e.id !== eventId));
            setDeletingEventId(null);
            
            if (selectedEvent?.id === eventId) {
              setSelectedEvent(events[0] || null);
            }
            
            setConfirmModal({
              visible: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Event wurde gelöscht.',
              variant: 'single'
            });
          }, 300); // Animation duration
        } catch (error) {
          console.error('Error deleting event:', error);
          setDeletingEventId(null);
          setConfirmModal({
            visible: true,
            type: 'error',
            title: 'Fehler',
            message: 'Event konnte nicht gelöscht werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  const openDocumentModal = async (event: Event) => {
    setDocumentEvent(event);
    setDocumentSearch('');
    setDocumentsLoading(true);
    try {
      const response = await apiClient.get('/api/documents');
      setAvailableDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      addToast('error', 'Dokumente konnten nicht geladen werden.');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const attachDocumentToEvent = async (document: EventDocument | null) => {
    if (!documentEvent) return;
    setDocumentSaving(true);
    try {
      await apiClient.put(`/api/events/${documentEvent.id}/document`, {
        document_id: document?.id || null
      });
      const updatedEvent = {
        ...documentEvent,
        document_id: document?.id || null,
        document_name: document?.name || null,
        document_path: document?.file_path || null
      };
      setEvents(prev => prev.map(event => event.id === updatedEvent.id ? updatedEvent : event));
      setDocumentEvent(updatedEvent);
      addToast('success', document ? 'Beleg wurde mit dem Event verknüpft.' : 'Beleg wurde vom Event entfernt.');
    } catch (error) {
      console.error('Error linking document:', error);
      addToast('error', 'Beleg konnte nicht verknüpft werden.');
    } finally {
      setDocumentSaving(false);
    }
  };

  /**
   * Betrag anlegen oder ändern.  unterdrückt den Einzel-Toast, damit die
   * Schnelleingabe am Ende nur eine Sammelmeldung zeigt.
   */
  const handleAmountSubmit = async (
    memberId: number,
    eventId: number,
    amount: string,
    silent = false,
  ): Promise<number | null> => {
    const numAmount = parseFloat(String(amount).replace(',', '.'));
    if (isNaN(numAmount) || numAmount <= 0) {
      if (!silent) addToast('warning', 'Bitte geben Sie einen gültigen Betrag ein.');
      return null;
    }

    // Set loading state for this cell
    const cellKey = `${memberId}-${eventId}`;
    setSavingCells(prev => new Set(prev).add(cellKey));

    try {
      const existingEntry = drinkEntries.find(
        e => e.member_id === memberId && e.month === eventId
      );

      let responseData: any;
      // ID des betroffenen Eintrags — die Schnelleingabe braucht sie sofort,
      // um anschließend als bezahlt markieren zu können (State ist dann noch alt).
      let affectedId: number | null = null;
      if (existingEntry) {
        affectedId = existingEntry.id;
        // Update existing entry - using POST instead of PUT
        // If amount changes, reset paid status to 0
        const shouldResetPaid = existingEntry.amount !== numAmount && existingEntry.paid === 1;
        const response = await apiClient.post('/api/drinks', {
          id: existingEntry.id,
          member_id: memberId,
          month: eventId,
          amount: numAmount,
          paid: shouldResetPaid ? 0 : existingEntry.paid
        });
        responseData = response.data;

        // Handle backend response - only update if success
        if (responseData.success) {
          // Update local state immediately - reset paid status if amount changed
          setDrinkEntries(prev => prev.map(entry =>
            entry.id === existingEntry.id
              ? {
                  ...entry,
                  amount: numAmount,
                  paid: shouldResetPaid ? 0 : existingEntry.paid,
                  paid_date: shouldResetPaid ? undefined : existingEntry.paid_date,
                  payment_method: shouldResetPaid ? undefined : existingEntry.payment_method
                }
              : entry
          ));

          // Update groupedData as well
          setGroupedData(prev => prev.map(member => {
            if (member.member_id === memberId) {
              return {
                ...member,
                drinks: member.drinks.map(drink =>
                  drink.event_id === eventId
                    ? {
                        ...drink,
                        amount: numAmount,
                        paid: shouldResetPaid ? 0 : drink.paid,
                        paid_date: shouldResetPaid ? undefined : drink.paid_date,
                        payment_method: shouldResetPaid ? undefined : drink.payment_method
                      }
                    : drink
                ),
                totals: {
                  ...member.totals,
                  total: member.drinks.reduce((sum, d) =>
                    sum + (d.event_id === eventId ? numAmount : d.amount), 0
                  ),
                  paid: member.drinks.reduce((sum, d) => {
                    if (d.event_id === eventId) {
                      return sum + (shouldResetPaid ? 0 : d.paid * numAmount);
                    }
                    return sum + (d.paid * d.amount);
                  }, 0),
                  open: member.drinks.reduce((sum, d) => {
                    if (d.event_id === eventId) {
                      return sum + (shouldResetPaid ? numAmount : (1 - d.paid) * numAmount);
                    }
                    return sum + ((1 - d.paid) * d.amount);
                  }, 0)
                }
              };
            }
            return member;
          }));
        }
      } else {
        // Create new entry
        const response = await apiClient.post('/api/drinks', {
          member_id: memberId,
          month: eventId,
          amount: numAmount,
          paid: 0
        });
        responseData = response.data;

        // Handle new backend response format
        if (responseData.success && responseData.drinkId) {
          // New format: {success: true, drinkId: 593, updated: false}
          affectedId = responseData.drinkId;
          const event = events.find(e => e.id === eventId);
          const newEntry: DrinkEntry = {
            id: responseData.drinkId,
            member_id: memberId,
            month: eventId,
            amount: numAmount,
            paid: 0,
            paid_date: undefined,
            paid_by: undefined,
            payment_method: undefined,
            token: null,
            event_label: event?.beschriftung || `Event ${eventId}`
          };

          setDrinkEntries(prev => [...prev, newEntry]);

          // Update or add to groupedData
          setGroupedData(prev => {
            const memberIndex = prev.findIndex(m => m.member_id === memberId);
            if (memberIndex >= 0) {
              // Member exists in grouped data, add new drink
              const updatedMember = { ...prev[memberIndex] };
              updatedMember.drinks = [...updatedMember.drinks, {
                drink_id: responseData.drinkId,
                event_id: eventId,
                event_label: event?.beschriftung || `Event ${eventId}`,
                amount: numAmount,
                paid: 0,
                paid_date: undefined,
                payment_method: undefined,
                token: null
              }];
              updatedMember.totals = {
                total: updatedMember.totals.total + numAmount,
                paid: updatedMember.totals.paid,
                open: updatedMember.totals.open + numAmount
              };

              const newGroupedData = [...prev];
              newGroupedData[memberIndex] = updatedMember;
              return newGroupedData;
            } else {
              // Member not in grouped data, add them
              const member = members.find(m => m.mg_nr === memberId);
              if (member) {
                return [...prev, {
                  member_id: memberId,
                  name: member.name,
                  vorname: member.vorname,
                  email: member.email,
                  foto_path: member.foto_path,
                  foto_path_updated: member.foto_path_updated,
                  drinks: [{
                    drink_id: responseData.drinkId,
                    event_id: eventId,
                    event_label: event?.beschriftung || `Event ${eventId}`,
                    amount: numAmount,
                    paid: 0,
                    paid_date: undefined,
                    payment_method: undefined,
                    token: null
                  }],
                  totals: {
                    total: numAmount,
                    paid: 0,
                    open: numAmount
                  }
                }];
              }
              return prev;
            }
          });
        } else if (responseData.id) {
          // Old format fallback
          affectedId = responseData.id;
          const event = events.find(e => e.id === eventId);
          const newEntry: DrinkEntry = {
            id: responseData.id,
            member_id: memberId,
            month: eventId,
            amount: numAmount,
            paid: responseData.paid || 0,
            paid_date: responseData.paid_date || undefined,
            paid_by: responseData.paid_by || undefined,
            payment_method: responseData.payment_method || undefined,
            token: responseData.token || null,
            event_label: event?.beschriftung || `Event ${eventId}`
          };

          setDrinkEntries(prev => [...prev, newEntry]);
        }
      }

      setEditingEntry(null);
      if (!silent) addToast('success', 'Betrag wurde gespeichert.');
      return affectedId;
    } catch (error) {
      console.error('Error saving amount:', error);
      if (!silent) addToast('error', 'Betrag konnte nicht gespeichert werden.');
      return null;
    } finally {
      // Remove loading state
      setSavingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  };

  const handleDeleteEntry = (entry: DrinkEntry) => {
    const member = members.find(m => m.mg_nr === entry.member_id);
    const memberName = member ? `${member.vorname} ${member.name}` : 'das Mitglied';
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Betrag löschen',
      message: entry.paid
        ? `Betrag von €${entry.amount.toFixed(2)} für ${memberName} wirklich löschen?`
        : `Betrag von €${entry.amount.toFixed(2)} für ${memberName} wirklich löschen? ${memberName} wird per E-Mail informiert, dass die vorherige Zahlungsaufforderung hinfällig ist.`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/api/drinks/${entry.id}`);

          // Eintrag aus lokalem State entfernen
          setDrinkEntries(prev => prev.filter(e => e.id !== entry.id));

          // groupedData aktualisieren (Drink entfernen + Summen neu berechnen)
          setGroupedData(prev => prev.map(m => {
            if (m.member_id !== entry.member_id) return m;
            const updatedDrinks = m.drinks.filter(d => d.event_id !== entry.month);
            const total = updatedDrinks.reduce((sum, d) => sum + d.amount, 0);
            const paid = updatedDrinks.reduce((sum, d) => sum + (d.paid * d.amount), 0);
            return { ...m, drinks: updatedDrinks, totals: { total, paid, open: total - paid } };
          }));

          setConfirmModal(prev => ({ ...prev, visible: false }));
          addToast('success', res.data?.mailed ? 'Betrag gelöscht. Korrektur-Mail wurde gesendet.' : 'Betrag gelöscht.');
        } catch (error) {
          console.error('Fehler beim Löschen des Betrags:', error);
          addToast('error', 'Fehler beim Löschen des Betrags.');
        }
      }
    });
  };

  /**
   * Eintrag als bezahlt markieren.
   *
   * `known` übergibt Mitglied, Event und Betrag direkt. Das ist nötig, wenn der
   * Eintrag gerade erst gespeichert wurde: `drinkEntries` ist dann noch der alte
   * Stand, und der Betrag aus dem State würde die frische Summe überschreiben.
   */
  const handleMarkAsPaid = async (
    entryId: number,
    method: PaymentMethod,
    silent = false,
    known?: { member_id: number; month: number; amount: number },
  ) => {
    const entry = known
      ? { id: entryId, ...known }
      : drinkEntries.find(e => e.id === entryId);
    if (!entry) {
      addToast('error', 'Eintrag nicht gefunden.');
      return;
    }

    // Set loading state for this cell
    const cellKey = `${entry.member_id}-${entry.month}`;
    setSavingCells(prev => new Set(prev).add(cellKey));

    try {
      const paidDate = new Date().toISOString();

      await apiClient.post('/api/drinks', {
        id: entryId,
        member_id: entry.member_id,
        month: entry.month,
        amount: entry.amount,
        paid: 1,
        paid_date: paidDate,
        payment_method: method
      });

      // Optimistic UI update - update the entry immediately
      setDrinkEntries(prev => prev.map(e =>
        e.id === entryId
          ? { ...e, paid: 1, paid_date: paidDate, payment_method: method }
          : e
      ));

      // Update groupedData as well
      setGroupedData(prev => prev.map(member => {
        if (member.member_id === entry.member_id) {
          const updatedDrinks = member.drinks.map(drink =>
            drink.event_id === entry.month
              ? {
                  ...drink,
                  paid: 1,
                  paid_date: paidDate,
                  payment_method: method
                }
              : drink
          );

          // Recalculate totals
          const total = updatedDrinks.reduce((sum, d) => sum + d.amount, 0);
          const paid = updatedDrinks.reduce((sum, d) => sum + (d.paid * d.amount), 0);
          const open = total - paid;

          return {
            ...member,
            drinks: updatedDrinks,
            totals: { total, paid, open }
          };
        }
        return member;
      }));

      // Statistics will be recalculated on next render via calculateStats()
      if (!silent) addToast('success', `Betrag wurde als bezahlt markiert (${method === 'cash' ? 'Bar' : 'PayPal'}).`);
    } catch (error) {
      console.error('Error marking as paid:', error);
      if (!silent) addToast('error', 'Status konnte nicht aktualisiert werden.');
    } finally {
      // Remove loading state
      setSavingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  };

  const availableYears = useMemo(() => Array.from(new Set(
    events
      .map(event => event.abrechnungsmonat ? new Date(event.abrechnungsmonat).getFullYear() : null)
      .filter((year): year is number => year !== null && !Number.isNaN(year))
  )).sort((a, b) => b - a), [events]);

  const yearEvents = useMemo(() => events
    .filter(event => selectedYear === 'all' || (
      event.abrechnungsmonat && new Date(event.abrechnungsmonat).getFullYear() === selectedYear
    ))
    .sort((a, b) => {
      const aTime = a.abrechnungsmonat ? new Date(a.abrechnungsmonat).getTime() : 0;
      const bTime = b.abrechnungsmonat ? new Date(b.abrechnungsmonat).getTime() : 0;
      return bTime - aTime || b.id - a.id;
    }), [events, selectedYear]);

  const yearEventIds = useMemo(() => new Set(yearEvents.map(event => event.id)), [yearEvents]);
  const yearDrinkEntries = useMemo(() => drinkEntries.filter(entry => yearEventIds.has(entry.month)), [drinkEntries, yearEventIds]);

  // Schnelleingabe läuft nur über echte Mitglieder – keine Gäste.
  const quickEntryMembers = useMemo(() => members.filter(m => !m.is_guest &&
    mayRecordConsumption(m, events.find(event => event.id === quickEntryEventId)?.abrechnungsmonat)), [members, events, quickEntryEventId]);

  const formatEventMonth = (event: Event) => event.abrechnungsmonat
    ? new Intl.DateTimeFormat('de-DE', { month: 'short', year: 'numeric' }).format(new Date(event.abrechnungsmonat))
    : 'Datum fehlt';

  // Filter members based on selected filter
  const getFilteredMembers = () => {
    let filtered = members.filter(member => !member.geloescht || yearDrinkEntries.some(entry => entry.member_id === member.mg_nr)); // Create a copy to avoid mutations

    // Guest filter - hide ALL guests when checkbox is not checked
    if (!showGuests) {
      filtered = filtered.filter(member => {
        // Only show non-guests when showGuests is false
        return member.is_guest !== true;
      });
    }
    // If showGuests is true, show all members including guests

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(member =>
        `${member.vorname} ${member.name}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter for matrix view
    if (filter !== 'all' && filter !== 'hide_paid_events') {
      switch (filter) {
        case 'open':
          // Only members with unpaid entries
          filtered = filtered.filter(member =>
            yearDrinkEntries.some(e => e.member_id === member.mg_nr && e.paid === 0)
          );
          break;
        case 'with_entries':
          // Only members with any entries
          filtered = filtered.filter(member =>
            yearDrinkEntries.some(e => e.member_id === member.mg_nr)
          );
          break;
      }
    }

    return filtered;
  };

  // Calculate statistics
  const calculateStats = () => {
    const total = yearDrinkEntries.reduce((sum, e) => sum + e.amount, 0);
    const paid = yearDrinkEntries.filter(e => e.paid === 1).reduce((sum, e) => sum + e.amount, 0);
    const open = total - paid;
    const membersWithEntries = new Set(yearDrinkEntries.map(e => e.member_id)).size;

    // Count total members (excluding guests)
    const totalMembers = members.filter(m => !m.is_guest && !m.geloescht).length;

    return { total, paid, open, members: membersWithEntries, totalMembers };
  };

  // Get visible events (for matrix columns)
  const getVisibleEvents = () => {
    let visibleEvents = yearEvents;
    
    // Filter out fully paid events if filter is active
    if (filter === 'hide_paid_events' || filter === 'open') {
      visibleEvents = visibleEvents.filter(event => {
        const eventEntries = yearDrinkEntries.filter(e => e.month === event.id);
        // Keep event if it has any unpaid entries
        return eventEntries.some(e => e.paid === 0);
      });
    }
    
    return visibleEvents; // Show all events
  };

  const stats = calculateStats();
  const filteredMembers = getFilteredMembers();

  const getMemberImageUrl = (member: Member) => {
    if (!member.foto_path) return null;
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${member.mg_nr}?size=S&token=${token}&ts=${member.foto_path_updated || Date.now()}`;
  };

  const visibleEvents = getVisibleEvents();

  // Quick Entry Functions
  const handleQuickEntryOpen = () => {
    if (visibleEvents.length === 0) {
      setConfirmModal({
        visible: true,
        type: 'warning',
        title: 'Hinweis',
        message: 'Bitte erstellen Sie zuerst ein Event.',
        variant: 'single'
      });
      return;
    }
    setQuickEntryEventId(visibleEvents[0].id);
    setQeAmounts({});
    setQeMethod('cash');
    setShowQuickEntryModal(true);
    // Erst nach dem Öffnen existieren die Felder.
    setTimeout(() => focusQuickEntryRow(0), 60);
  };

  /**
   * Schnelleingabe speichern: alle erfassten Beträge in einem Durchgang.
   *
   * Nutzt `handleAmountSubmit` (legt an oder ändert und setzt bei Betragsänderung
   * den Bezahlt-Status zurück) und markiert danach optional als bezahlt, je nach
   * gewählter Zahlweise. „noch offen" lässt die Einträge unbezahlt.
   */
  const handleQuickEntrySave = async () => {
    if (!quickEntryEventId) return;
    const rows = Object.entries(qeAmounts)
      .map(([memberId, raw]) => ({ memberId: Number(memberId), value: parseAmount(raw) }))
      .filter(row => row.value > 0);

    if (rows.length === 0) {
      addToast('warning', 'Es wurden keine Beträge erfasst.');
      return;
    }

    setQeSaving(true);
    try {
      // IDs der gespeicherten Einträge sammeln — der State ist hier noch nicht
      // aktualisiert, deshalb liefert handleAmountSubmit die ID direkt zurück.
      const saved: { id: number; memberId: number; value: number }[] = [];
      for (const row of rows) {
        const id = await handleAmountSubmit(row.memberId, quickEntryEventId, String(row.value), true);
        if (id != null) saved.push({ id, memberId: row.memberId, value: row.value });
      }

      if (qeMethod !== 'open') {
        const method: PaymentMethod = qeMethod;
        for (const item of saved) {
          await handleMarkAsPaid(item.id, method, true, {
            member_id: item.memberId,
            month: quickEntryEventId,
            amount: item.value,
          });
        }
      }

      addToast('success', `${rows.length} ${rows.length === 1 ? 'Betrag' : 'Beträge'} gespeichert.`);
      setQeAmounts({});
      setShowQuickEntryModal(false);
      setExpandedEvents(prev => ({ ...prev, [quickEntryEventId]: true }));
    } catch (error) {
      console.error('Fehler beim Speichern der Schnelleingabe:', error);
      addToast('error', 'Nicht alle Beträge konnten gespeichert werden.');
    } finally {
      setQeSaving(false);
    }
  };

  /** Betrag einer Zeile setzen; 0 oder weniger entfernt den Eintrag. */
  const setQuickEntryAmount = (memberId: number, value: number) => {
    setQeAmounts(prev => {
      const next = { ...prev };
      if (value <= 0) delete next[memberId];
      else next[memberId] = value.toFixed(2).replace('.', ',');
      return next;
    });
  };

  /** Fokus auf die n-te Betragszeile der Schnelleingabe (mit Umlauf). */
  const focusQuickEntryRow = (index: number) => {
    const nodes = document.querySelectorAll<HTMLInputElement>('[data-qe-input]');
    if (!nodes.length) return;
    const target = nodes[(index + nodes.length) % nodes.length];
    if (target) {
      target.focus();
      target.select();
    }
  };
  /* ------------------------------------------------------------------ *
   * View-Models für die Akkordeon-Ansichten (Design-Entwurf)
   * ------------------------------------------------------------------ */

  /** Zeilen der Mitglieder-Ansicht: Summen, Fortschritt, Status, Einträge. */
  const memberRows = useMemo(() => filteredMembers.map(member => {
    const entries = yearDrinkEntries
      .filter(e => e.member_id === member.mg_nr)
      .sort((a, b) => {
        const ea = yearEvents.find(ev => ev.id === a.month);
        const eb = yearEvents.find(ev => ev.id === b.month);
        const ta = ea?.abrechnungsmonat ? new Date(ea.abrechnungsmonat).getTime() : 0;
        const tb = eb?.abrechnungsmonat ? new Date(eb.abrechnungsmonat).getTime() : 0;
        return tb - ta;
      });
    const total = entries.reduce((sum, e) => sum + e.amount, 0);
    const paid = entries.filter(e => e.paid === 1).reduce((sum, e) => sum + e.amount, 0);
    const open = total - paid;
    return {
      member,
      entries,
      total,
      open,
      pct: total === 0 ? 0 : Math.round((paid / total) * 100),
    };
  }), [filteredMembers, yearDrinkEntries, yearEvents]);

  /** Zeilen der Event-Ansicht: alle Einträge eines Events über die Mitglieder. */
  const eventRows = useMemo(() => visibleEvents.map(event => {
    const entries = yearDrinkEntries
      .filter(e => e.month === event.id)
      .filter(e => {
        const m = members.find(x => x.mg_nr === e.member_id);
        if (!m) return false;
        if (!showGuests && m.is_guest === true) return false;
        if (!searchTerm) return true;
        return `${m.vorname} ${m.name}`.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => {
        const ma = members.find(x => x.mg_nr === a.member_id);
        const mb = members.find(x => x.mg_nr === b.member_id);
        return `${ma?.vorname} ${ma?.name}`.localeCompare(`${mb?.vorname} ${mb?.name}`, 'de');
      });
    const total = entries.reduce((sum, e) => sum + e.amount, 0);
    const paidCount = entries.filter(e => e.paid === 1).length;
    const open = total - entries.filter(e => e.paid === 1).reduce((sum, e) => sum + e.amount, 0);
    return {
      event,
      entries,
      total,
      open,
      paidCount,
      pct: entries.length === 0 ? 0 : Math.round((paidCount / entries.length) * 100),
      withoutEntry: filteredMembers.length - entries.length,
    };
  }).filter(row => {
    if (filter === 'open') return row.open > 0;
    if (filter === 'with_entries') return row.entries.length > 0;
    return true;
  }), [visibleEvents, yearDrinkEntries, members, showGuests, searchTerm, filteredMembers.length, filter]);

  /** Alle Einträge eines Events als bezahlt markieren. */
  const handleMarkEventPaid = (event: Event, entries: DrinkEntry[]) => {
    const open = entries.filter(e => e.paid !== 1);
    if (open.length === 0) {
      addToast('info', 'Für dieses Event ist bereits alles bezahlt.');
      return;
    }
    setConfirmModal({
      visible: true,
      type: 'question',
      title: 'Alle als bezahlt markieren',
      message: `${open.length} offene ${open.length === 1 ? 'Buchung' : 'Buchungen'} von „${event.beschriftung}" als bar bezahlt markieren?`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, visible: false }));
        for (const entry of open) {
          await handleMarkAsPaid(entry.id, 'cash', true);
        }
        addToast('success', `${open.length} ${open.length === 1 ? 'Buchung' : 'Buchungen'} als bezahlt markiert.`);
      },
    });
  };

  const toggleMember = (id: number) =>
    setExpandedMembers(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleEvent = (id: number) =>
    setExpandedEvents(prev => ({ ...prev, [id]: !prev[id] }));

  const eventLabelFor = (eventId: number) =>
    yearEvents.find(e => e.id === eventId)?.beschriftung || `Event ${eventId}`;

  const paymentLabel = (entry: DrinkEntry) => {
    if (entry.paid !== 1) return 'noch offen';
    if (entry.payment_method === 'paypal') return 'PayPal';
    if (entry.payment_method === 'cash') return 'Bar';
    return 'Bezahlt';
  };

  const MODE_TABS: { key: ViewMode; label: string }[] = [
    { key: 'members', label: 'Nach Mitgliedern' },
    { key: 'events', label: 'Nach Events' },
    { key: 'matrix', label: 'Matrix' },
  ];

  const FILTER_CHIPS: { key: typeof filter; label: string }[] = viewMode === 'events'
    ? [
        { key: 'all', label: 'Alle' },
        { key: 'open', label: 'Mit offenen Beträgen' },
        { key: 'with_entries', label: 'Mit Einträgen' },
        { key: 'hide_paid_events', label: 'Bezahlte ausblenden' },
      ]
    : [
        { key: 'all', label: 'Alle' },
        { key: 'open', label: 'Nur Offene' },
        { key: 'with_entries', label: 'Mit Buchungen' },
        { key: 'hide_paid_events', label: 'Bezahlte Events ausblenden' },
      ];

  /** Statuspille: Text trägt die Information, Farbe unterstützt nur. */
  const StatusPill: React.FC<{ count: number; open: number }> = ({ count, open }) => {
    if (count === 0) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
          Keine Buchung
        </span>
      );
    }
    if (open > 0) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-error-50 px-2.5 py-1 text-xs font-semibold text-error-700 dark:bg-error-900/30 dark:text-error-300">
          <span className="h-1.5 w-1.5 rounded-full bg-error-500" />
          {formatCurrency(open)} offen
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700 dark:bg-success-900/30 dark:text-success-300">
        <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
        Vollständig bezahlt
      </span>
    );
  };

  /** Fortschrittsbalken mit Beschriftung darunter. */
  const Progress: React.FC<{ pct: number; label: string }> = ({ pct, label }) => (
    <div>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="h-full rounded-full bg-success-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );

  /** Betragszelle eines Eintrags — Klick öffnet die Inline-Bearbeitung. */
  const AmountCell: React.FC<{ entry: DrinkEntry }> = ({ entry }) => {
    const isEditing =
      editingEntry?.memberId === entry.member_id && editingEntry?.eventId === entry.month;
    const isSaving = savingCells.has(`${entry.member_id}-${entry.month}`);

    if (isEditing) {
      return (
        <input
          autoFocus
          inputMode="decimal"
          value={editingEntry.amount}
          onChange={e => setEditingEntry({ ...editingEntry, amount: e.target.value })}
          onBlur={() => handleAmountSubmit(entry.member_id, entry.month, editingEntry.amount)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAmountSubmit(entry.member_id, entry.month, editingEntry.amount);
            }
            if (e.key === 'Escape') setEditingEntry(null);
          }}
          className="h-8 w-full rounded-lg border border-primary-500 px-2 text-right text-[13px] font-semibold tabular-nums text-gray-900 outline-none ring-2 ring-primary-500/25 dark:bg-gray-800 dark:text-white"
          aria-label="Betrag bearbeiten"
        />
      );
    }

    return (
      <button
        type="button"
        disabled={isSaving}
        onClick={() =>
          setEditingEntry({
            memberId: entry.member_id,
            eventId: entry.month,
            amount: entry.amount.toFixed(2).replace('.', ','),
          })
        }
        title="Betrag bearbeiten"
        className={`h-8 w-full rounded-lg border border-transparent px-2 text-right text-[13px] font-semibold tabular-nums transition-colors hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-700/50 ${
          entry.paid === 1
            ? 'text-success-600 dark:text-success-400'
            : 'text-error-600 dark:text-error-400'
        }`}
      >
        {isSaving ? '…' : formatCurrency(entry.amount)}
      </button>
    );
  };

  /** Bezahl- und Löschaktionen eines Eintrags. */
  const EntryActions: React.FC<{ entry: DrinkEntry }> = ({ entry }) => {
    if (entry.paid === 1) {
      return (
        <div className="flex items-center justify-end gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-success-200 bg-success-50 px-3 py-1 text-xs font-semibold text-success-700 dark:border-success-800/60 dark:bg-success-900/30 dark:text-success-300">
            <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Bezahlt
          </span>
          <button
            type="button"
            onClick={() => handleDeleteEntry(entry)}
            title="Betrag löschen"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-900/20"
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => handleMarkAsPaid(entry.id, 'cash')}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-700 transition-colors hover:border-success-400 hover:bg-success-50 hover:text-success-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-success-900/20"
          title="Als bar bezahlt markieren"
        >
          <BanknotesIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Bar
        </button>
        <button
          type="button"
          onClick={() => handleMarkAsPaid(entry.id, 'paypal')}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-700 transition-colors hover:border-info-400 hover:bg-info-50 hover:text-info-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-info-900/20"
          title="Als per PayPal bezahlt markieren"
        >
          <CreditCardIcon className="h-3.5 w-3.5" aria-hidden="true" />
          PayPal
        </button>
        <button
          type="button"
          onClick={() => handleDeleteEntry(entry)}
          title="Betrag löschen"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-900/20"
        >
          <TrashIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  };

  /** Summe und Anzahl der in der Schnelleingabe erfassten Beträge. */
  const quickEntrySum = useMemo(
    () => Object.values(qeAmounts).reduce((sum, raw) => sum + parseAmount(raw), 0),
    [qeAmounts],
  );
  const quickEntryCount = useMemo(
    () => Object.values(qeAmounts).filter(raw => parseAmount(raw) > 0).length,
    [qeAmounts],
  );
  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-4 pb-8">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-[#1c2432] sm:p-7">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
            <div className="flex-1 space-y-2">
              <div className="h-6 w-44 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-56 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2">
                <div className="h-3 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-8 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432]"
            >
              <div className="h-9 w-9 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/5 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-1/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="h-6 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasEvents = events.length > 0;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 pb-8">
      {/* ---------------- Seitenkopf ---------------- */}
      <header className="app-page-header overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-4 p-5 sm:p-6 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <CurrencyEuroIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
                Getränkekasse
              </p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">
                Verzehrkosten
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Getränke und Event-Beiträge ·{' '}
                {selectedYear === 'all' ? 'alle Abrechnungsjahre' : `Abrechnungsjahr ${selectedYear}`}
              </p>
            </div>
          </div>

          {/* Suche + Aktionen */}
          <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap">
            <div className="relative w-full sm:w-52 xl:w-56">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Mitglied oder Event suchen…"
                aria-label="Suchen"
                className="h-11 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Suche zurücksetzen"
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowHelpModal(true)}
              title="Hilfe anzeigen"
              aria-label="Hilfe anzeigen"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              <DocumentArrowDownIcon className="h-5 w-5" aria-hidden="true" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              type="button"
              onClick={() => setShowNewEventModal(true)}
              className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
              Event
            </button>
            <button
              type="button"
              onClick={handleQuickEntryOpen}
              className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-bold text-white transition-colors hover:bg-primary-700"
            >
              <CurrencyEuroIcon className="h-5 w-5" aria-hidden="true" />
              Schnelleingabe
            </button>
          </div>
        </div>
      </header>

      {!hasEvents ? (
        /* ---------------- Leerzustand ---------------- */
        <section className="rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-[#1c2432]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400">
            <CalendarIcon className="h-7 w-7" aria-hidden="true" />
          </div>
          <h2 className="mt-4 text-lg font-bold text-gray-900 dark:text-white">
            Noch kein Event vorhanden
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
            Ein Event bündelt die Verzehrkosten eines Abrechnungsmonats. Lege zuerst ein Event an,
            danach kannst du Beträge über die Schnelleingabe erfassen.
          </p>
          <button
            type="button"
            onClick={() => setShowNewEventModal(true)}
            className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-primary-600 px-5 text-sm font-bold text-white transition-colors hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
            Erstes Event erstellen
          </button>
        </section>
      ) : (
        <>
          {/* ---------------- Kennzahlen ---------------- */}
          <section className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
            <div className="grid grid-cols-2 items-start gap-2 sm:grid-cols-3 sm:items-center sm:gap-6">
              <StatCard
                icon={CurrencyEuroIcon}
                label="Gesamt"
                value={formatCurrency(stats.total)}
                sub={`${stats.members} von ${stats.totalMembers} Mitgliedern mit Buchungen`}
                tone="primary"
              />
              <StatCard
                icon={CheckCircleIcon}
                label="Bezahlt"
                value={formatCurrency(stats.paid)}
                sub={`${stats.total === 0 ? 0 : Math.round((stats.paid / stats.total) * 100)} % des Gesamtbetrags`}
                tone="success"
                valueTone="success"
              />
              <StatCard
                icon={ExclamationCircleIcon}
                label="Offen"
                value={formatCurrency(stats.open)}
                sub={
                  stats.open <= 0
                    ? 'Alles beglichen'
                    : `${memberRows.filter(r => r.open > 0).length} Mitglieder offen`
                }
                tone="error"
                valueTone={stats.open > 0 ? 'error' : 'neutral'}
              />
            </div>

            {/* Bezahlt/Offen-Verhältnis über die volle Breite */}
            <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full bg-success-500"
                style={{ width: `${stats.total === 0 ? 0 : Math.round((stats.paid / stats.total) * 100)}%` }}
              />
              <div className="h-full flex-1 bg-error-500" />
            </div>
          </section>

          {/* ---------------- Ansicht, Filter, Zeitraum ---------------- */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
            <div className="flex flex-col gap-3 border-b border-gray-100 p-4 dark:border-gray-700/70 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                {/* Ansichtsmodus */}
                <div
                  role="tablist"
                  aria-label="Ansicht"
                  className="flex w-full gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-700 sm:w-fit"
                >
                  {MODE_TABS.map(tab => {
                    const on = viewMode === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => {
                          setViewMode(tab.key);
                          setEditingEntry(null);
                        }}
                        className={`flex min-h-10 min-w-0 flex-1 items-center justify-center rounded-lg px-2 py-1 text-center text-[11px] font-bold leading-tight transition-all sm:h-10 sm:flex-none sm:px-4 sm:py-0 sm:text-[13px] ${
                          on
                            ? 'bg-white text-gray-900 shadow-sm dark:bg-[#1c2432] dark:text-white'
                            : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Filter-Chips */}
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
                  {FILTER_CHIPS.map(chip => {
                    const on = filter === chip.key;
                    return (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={() => setFilter(chip.key)}
                        aria-pressed={on}
                        className={`h-8 shrink-0 rounded-full border px-3.5 text-[13px] font-semibold transition-colors ${
                          on
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {chip.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={showGuests}
                    onChange={e => setShowGuests(e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-primary-600"
                  />
                  Gäste einbeziehen
                </label>
                <div className="flex items-center gap-2">
                  <AttributeLabel
                    iconKey="year"
                    htmlFor="drink-year"
                    textClassName="text-[13px] font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Jahr
                  </AttributeLabel>
                  <select
                    id="drink-year"
                    value={selectedYear === 'all' ? 'all' : String(selectedYear)}
                    onChange={e =>
                      setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))
                    }
                    className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-[13px] font-semibold text-gray-900 focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {availableYears.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                    <option value="all">Alle Jahre</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ===================== Nach Mitgliedern ===================== */}
            {viewMode === 'members' && (
              <div>
                {/* Spaltenköpfe nur ab lg — mobil trägt jede Zeile ihre Angaben selbst */}
                <div className="hidden border-b border-gray-100 bg-gray-50 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700/70 dark:bg-gray-800/50 dark:text-gray-400 lg:grid lg:grid-cols-[minmax(0,1fr)_190px_128px_170px_34px] lg:gap-4">
                  <div>Mitglied</div>
                  <div>Fortschritt</div>
                  <div className="text-right">Gesamt</div>
                  <div className="text-right">Status</div>
                  <div />
                </div>

                {memberRows.length === 0 ? (
                  <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Keine Mitglieder für diese Auswahl.
                  </p>
                ) : (
                  <ul>
                    {memberRows.map(row => {
                      const isOpen = !!expandedMembers[row.member.mg_nr];
                      const fullName = `${row.member.vorname} ${row.member.name}`.trim();
                      return (
                        <li
                          key={row.member.mg_nr}
                          className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/70"
                        >
                          <button
                            type="button"
                            onClick={() => toggleMember(row.member.mg_nr)}
                            aria-expanded={isOpen}
                            className="grid w-full grid-cols-1 gap-2 px-5 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30 lg:grid-cols-[minmax(0,1fr)_190px_128px_170px_34px] lg:items-center lg:gap-4"
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <MemberAvatar
                                member={{ vorname: row.member.vorname, name: row.member.name }}
                                role={row.member.is_guest ? 'guest' : undefined}
                                photoUrl={getMemberImageUrl(row.member) || undefined}
                                hasImageError={!!imageErrors[`member-${row.member.mg_nr}`]}
                                onImageError={() =>
                                  setImageErrors(prev => ({ ...prev, [`member-${row.member.mg_nr}`]: true }))
                                }
                                size="sm"
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                    {fullName}
                                  </span>
                                  {row.member.is_guest && <GuestBadge />}
                                </div>
                                <div className="text-[11px] text-gray-400 dark:text-gray-500">
                                  Nr. {row.member.mitglied_nr || '—'} ·{' '}
                                  {row.entries.length === 1 ? '1 Buchung' : `${row.entries.length} Buchungen`}
                                </div>
                              </div>
                            </div>

                            <Progress
                              pct={row.pct}
                              label={
                                row.entries.length === 0
                                  ? 'Keine Buchungen'
                                  : row.open > 0
                                  ? `${row.pct} % bezahlt · ${formatCurrency(row.open)} offen`
                                  : 'Vollständig bezahlt'
                              }
                            />

                            <div
                              className={`text-sm font-semibold tabular-nums lg:text-right ${
                                row.entries.length === 0
                                  ? 'text-gray-400 dark:text-gray-500'
                                  : 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {row.entries.length === 0 ? '—' : formatCurrency(row.total)}
                            </div>

                            <div className="flex lg:justify-end">
                              <StatusPill count={row.entries.length} open={row.open} />
                            </div>

                            <div className="hidden text-gray-400 lg:flex lg:justify-end dark:text-gray-500">
                              <ChevronDownIcon
                                className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                aria-hidden="true"
                              />
                            </div>
                          </button>

                          {isOpen && (
                            <div className="border-t border-primary-100 bg-primary-50/40 px-4 py-3 dark:border-primary-900/40 dark:bg-primary-900/10 sm:pl-[66px]">
                              {row.entries.length === 0 ? (
                                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-primary-200 bg-white p-4 dark:border-primary-900/50 dark:bg-[#1c2432]">
                                  <p className="text-[13px] text-gray-500 dark:text-gray-400">
                                    Keine Buchungen{' '}
                                    {selectedYear === 'all' ? 'vorhanden' : `im Abrechnungsjahr ${selectedYear}`}.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={handleQuickEntryOpen}
                                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary-50 px-3 text-xs font-bold text-primary-700 transition-colors hover:bg-primary-100 dark:bg-primary-900/25 dark:text-primary-300"
                                  >
                                    <PlusIcon className="h-4 w-4" aria-hidden="true" />
                                    Buchung erfassen
                                  </button>
                                </div>
                              ) : (
                                <ul className="space-y-2">
                                  {row.entries.map(entry => (
                                    <li
                                      key={entry.id}
                                      className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-[#1c2432] sm:grid-cols-[minmax(0,1fr)_110px_118px_minmax(0,210px)] sm:items-center sm:gap-4"
                                    >
                                      <div className="min-w-0">
                                        <div className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                                          {eventLabelFor(entry.month)}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                          {formatEventMonth(
                                            yearEvents.find(e => e.id === entry.month) || { id: entry.month } as Event,
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {paymentLabel(entry)}
                                      </div>
                                      <AmountCell entry={entry} />
                                      <EntryActions entry={entry} />
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* ===================== Nach Events ===================== */}
            {viewMode === 'events' && (
              <div>
                <div className="hidden border-b border-gray-100 bg-gray-50 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700/70 dark:bg-gray-800/50 dark:text-gray-400 lg:grid lg:grid-cols-[minmax(0,1fr)_220px_128px_170px_34px] lg:gap-4">
                  <div>Event</div>
                  <div>Fortschritt</div>
                  <div className="text-right">Gesamt</div>
                  <div className="text-right">Status</div>
                  <div />
                </div>

                {eventRows.length === 0 ? (
                  <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Keine Events für diese Auswahl.
                  </p>
                ) : (
                  <ul>
                    {eventRows.map(row => {
                      const isOpen = !!expandedEvents[row.event.id];
                      return (
                        <li
                          key={row.event.id}
                          className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/70"
                        >
                          <div className="grid grid-cols-1 gap-2 px-5 py-3 lg:grid-cols-[minmax(0,1fr)_220px_128px_170px_34px] lg:items-center lg:gap-4">
                            <button
                              type="button"
                              onClick={() => toggleEvent(row.event.id)}
                              aria-expanded={isOpen}
                              className="flex min-w-0 items-center gap-3 rounded-lg text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30"
                            >
                              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                                <CurrencyEuroIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-bold text-gray-900 dark:text-white">
                                  {row.event.beschriftung}
                                </span>
                                <span className="block text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                  {formatEventMonth(row.event)} ·{' '}
                                  {row.entries.length === 1 ? '1 Eintrag' : `${row.entries.length} Einträge`}
                                </span>
                              </span>
                            </button>

                            <Progress
                              pct={row.pct}
                              label={
                                row.entries.length === 0
                                  ? 'Noch keine Einträge'
                                  : `${row.paidCount} von ${row.entries.length} bezahlt · ${row.pct} %`
                              }
                            />

                            <div
                              className={`text-sm font-semibold tabular-nums lg:text-right ${
                                row.entries.length === 0
                                  ? 'text-gray-400 dark:text-gray-500'
                                  : 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {row.entries.length === 0 ? '—' : formatCurrency(row.total)}
                            </div>

                            <div className="flex lg:justify-end">
                              <StatusPill count={row.entries.length} open={row.open} />
                            </div>

                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => openDocumentModal(row.event)}
                                title={row.event.document_id ? 'Beleg ansehen oder ändern' : 'Beleg verknüpfen'}
                                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                                  row.event.document_id
                                    ? 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20'
                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteEvent(row.event.id)}
                                title="Event löschen"
                                disabled={deletingEventId === row.event.id}
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error-50 hover:text-error-600 disabled:opacity-50 dark:hover:bg-error-900/20"
                              >
                                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleEvent(row.event.id)}
                                aria-label={isOpen ? 'Einträge einklappen' : 'Einträge ausklappen'}
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <ChevronDownIcon
                                  className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>

                          {isOpen && (
                            <div className="border-t border-primary-100 bg-primary-50/40 px-4 py-3 dark:border-primary-900/40 dark:bg-primary-900/10 sm:pl-[66px]">
                              <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                  Einträge
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openDocumentModal(row.event)}
                                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                  >
                                    <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
                                    Beleg
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMarkEventPaid(row.event, row.entries)}
                                    disabled={row.open <= 0}
                                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-xs font-bold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                                  >
                                    <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                                    Alle als bezahlt
                                  </button>
                                </div>
                              </div>

                              {row.entries.length === 0 ? (
                                <p className="rounded-lg border border-dashed border-primary-200 bg-white p-4 text-[13px] text-gray-500 dark:border-primary-900/50 dark:bg-[#1c2432] dark:text-gray-400">
                                  Für dieses Event wurden noch keine Beträge erfasst.
                                </p>
                              ) : (
                                <ul className="space-y-2">
                                  {row.entries.map(entry => {
                                    const m = members.find(x => x.mg_nr === entry.member_id);
                                    return (
                                      <li
                                        key={entry.id}
                                        className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-[#1c2432] sm:grid-cols-[minmax(0,1fr)_110px_118px_minmax(0,210px)] sm:items-center sm:gap-4"
                                      >
                                        <div className="flex min-w-0 items-center gap-2.5">
                                          <MemberAvatar
                                            member={{ vorname: m?.vorname, name: m?.name }}
                                            role={m?.is_guest ? 'guest' : undefined}
                                            photoUrl={(m && getMemberImageUrl(m)) || undefined}
                                            hasImageError={!!imageErrors[`member-${entry.member_id}`]}
                                            onImageError={() =>
                                              setImageErrors(prev => ({ ...prev, [`member-${entry.member_id}`]: true }))
                                            }
                                            size="sm"
                                          />
                                          <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                                            {m ? `${m.vorname} ${m.name}`.trim() : `Mitglied ${entry.member_id}`}
                                          </span>
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                          {paymentLabel(entry)}
                                        </div>
                                        <AmountCell entry={entry} />
                                        <EntryActions entry={entry} />
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}

                              <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-2.5">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {row.withoutEntry > 0
                                    ? `${row.withoutEntry} ${row.withoutEntry === 1 ? 'Mitglied' : 'Mitglieder'} ohne Eintrag`
                                    : 'Alle Mitglieder haben einen Eintrag'}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setQuickEntryEventId(row.event.id);
                                    setQeAmounts({});
                                    setQeMethod('cash');
                                    setShowQuickEntryModal(true);
                                    setTimeout(() => focusQuickEntryRow(0), 60);
                                  }}
                                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary-50 px-3 text-xs font-bold text-primary-700 transition-colors hover:bg-primary-100 dark:bg-primary-900/25 dark:text-primary-300"
                                >
                                  <UserPlusIcon className="h-4 w-4" aria-hidden="true" />
                                  Mitglieder erfassen
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* ===================== Matrix ===================== */}
            {viewMode === 'matrix' && (
              <div className="overflow-x-auto">
                {visibleEvents.length === 0 ? (
                  <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Keine Events für diese Auswahl.
                  </p>
                ) : (
                  <table className="w-full min-w-[640px] border-collapse">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50 dark:border-gray-700/70 dark:bg-gray-800/50">
                        <th className="sticky left-0 z-10 bg-gray-50 px-5 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                          Mitglied
                        </th>
                        {visibleEvents.map(event => (
                          <th
                            key={event.id}
                            className="px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span className="max-w-[130px] truncate normal-case" title={event.beschriftung}>
                                {event.beschriftung}
                              </span>
                              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                                {formatEventMonth(event)}
                              </span>
                              <span className="flex items-center gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => openDocumentModal(event)}
                                  title={event.document_id ? 'Beleg ansehen oder ändern' : 'Beleg verknüpfen'}
                                  className={`flex h-6 w-6 items-center justify-center rounded transition-colors ${
                                    event.document_id
                                      ? 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20'
                                      : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  <PaperClipIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteEvent(event.id)}
                                  disabled={deletingEventId === event.id}
                                  title="Event löschen"
                                  className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-error-50 hover:text-error-600 disabled:opacity-50 dark:hover:bg-error-900/20"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                              </span>
                            </div>
                          </th>
                        ))}
                        <th className="px-4 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Summe
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberRows.map(row => {
                        const totals = { total: row.total, open: row.open };
                        return (
                          <tr
                            key={row.member.mg_nr}
                            className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 dark:border-gray-700/70 dark:hover:bg-gray-700/30"
                          >
                            <td className="sticky left-0 z-10 bg-white px-5 py-2 dark:bg-[#1c2432]">
                              <div className="flex min-w-0 items-center gap-2.5">
                                <MemberAvatar
                                  member={{ vorname: row.member.vorname, name: row.member.name }}
                                  role={row.member.is_guest ? 'guest' : undefined}
                                  photoUrl={getMemberImageUrl(row.member) || undefined}
                                  hasImageError={!!imageErrors[`member-${row.member.mg_nr}`]}
                                  onImageError={() =>
                                    setImageErrors(prev => ({ ...prev, [`member-${row.member.mg_nr}`]: true }))
                                  }
                                  size="sm"
                                />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                                      {`${row.member.vorname} ${row.member.name}`.trim()}
                                      {(row.member.geloescht || row.member.austritt_am) && <span className="ml-2 text-xs text-warning-700 dark:text-warning-300">{membershipStatus(row.member)}</span>}
                                    </span>
                                    {row.member.is_guest && <GuestBadge />}
                                  </div>
                                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                    Nr. {row.member.mitglied_nr || '—'}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {visibleEvents.map(event => {
                              const entry = row.entries.find(e => e.month === event.id);
                              const isEditing =
                                editingEntry?.memberId === row.member.mg_nr &&
                                editingEntry?.eventId === event.id;
                              const isSaving = savingCells.has(`${row.member.mg_nr}-${event.id}`);

                              return (
                                <td key={event.id} className="px-2 py-2 text-center">
                                  {isEditing ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <input
                                        autoFocus
                                        inputMode="decimal"
                                        value={editingEntry.amount}
                                        onChange={e =>
                                          setEditingEntry({ ...editingEntry, amount: e.target.value })
                                        }
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAmountSubmit(row.member.mg_nr, event.id, editingEntry.amount);
                                          }
                                          if (e.key === 'Escape') setEditingEntry(null);
                                        }}
                                        className="h-8 w-20 rounded-lg border border-primary-500 px-2 text-right text-[13px] font-semibold tabular-nums text-gray-900 outline-none ring-2 ring-primary-500/25 dark:bg-gray-800 dark:text-white"
                                        aria-label="Betrag"
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleAmountSubmit(row.member.mg_nr, event.id, editingEntry.amount)
                                        }
                                        title="Speichern"
                                        className="flex h-7 w-7 items-center justify-center rounded text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20"
                                      >
                                        <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingEntry(null)}
                                        title="Abbrechen"
                                        className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      >
                                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                      </button>
                                      {entry && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingEntry(null);
                                            handleDeleteEntry(entry);
                                          }}
                                          title="Betrag löschen"
                                          className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-900/20"
                                        >
                                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center gap-1">
                                      <button
                                        type="button"
                                        disabled={isSaving}
                                        onDoubleClick={() =>
                                          setEditingEntry({
                                            memberId: row.member.mg_nr,
                                            eventId: event.id,
                                            amount: entry ? entry.amount.toFixed(2).replace('.', ',') : '',
                                          })
                                        }
                                        onClick={() =>
                                          !entry &&
                                          setEditingEntry({
                                            memberId: row.member.mg_nr,
                                            eventId: event.id,
                                            amount: '',
                                          })
                                        }
                                        title={entry ? 'Doppelklick zum Bearbeiten' : 'Betrag erfassen'}
                                        className={`h-8 min-w-[76px] rounded-lg border border-transparent px-2 text-[13px] font-semibold tabular-nums transition-colors hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-700/50 ${
                                          !entry
                                            ? 'text-gray-300 dark:text-gray-600'
                                            : entry.paid === 1
                                            ? 'text-success-600 dark:text-success-400'
                                            : 'text-error-600 dark:text-error-400'
                                        }`}
                                      >
                                        {isSaving ? '…' : entry ? formatCurrency(entry.amount) : '+'}
                                      </button>
                                      {entry && entry.paid !== 1 && (
                                        <span className="flex items-center gap-0.5">
                                          <button
                                            type="button"
                                            onClick={() => handleMarkAsPaid(entry.id, 'cash')}
                                            title="Als bar bezahlt markieren"
                                            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-success-50 hover:text-success-600 dark:hover:bg-success-900/20"
                                          >
                                            <BanknotesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleMarkAsPaid(entry.id, 'paypal')}
                                            title="Als per PayPal bezahlt markieren"
                                            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-info-50 hover:text-info-600 dark:hover:bg-info-900/20"
                                          >
                                            <CreditCardIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>
                              );
                            })}

                            <td className="px-4 py-2 text-right">
                              <div
                                className={`text-[13px] font-bold tabular-nums ${
                                  totals.open > 0
                                    ? 'text-error-600 dark:text-error-400'
                                    : 'text-gray-900 dark:text-white'
                                }`}
                              >
                                {formatCurrency(totals.total)}
                              </div>
                              {totals.open > 0 && (
                                <div className="text-[11px] text-error-500 dark:text-error-400">
                                  {formatCurrency(totals.open)} offen
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/50">
                        <td className="sticky left-0 z-10 bg-gray-50 px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                          Summe
                        </td>
                        {visibleEvents.map(event => {
                          const eventEntries = yearDrinkEntries.filter(e => e.month === event.id);
                          const sum = eventEntries.reduce((s, e) => s + e.amount, 0);
                          const openSum = eventEntries
                            .filter(e => e.paid !== 1)
                            .reduce((s, e) => s + e.amount, 0);
                          return (
                            <td key={event.id} className="px-2 py-2.5 text-center">
                              <div className="text-[13px] font-bold tabular-nums text-gray-900 dark:text-white">
                                {formatCurrency(sum)}
                              </div>
                              {openSum > 0 && (
                                <div className="text-[11px] text-error-500 dark:text-error-400">
                                  {formatCurrency(openSum)} offen
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2.5 text-right">
                          <div className="text-[13px] font-bold tabular-nums text-gray-900 dark:text-white">
                            {formatCurrency(stats.total)}
                          </div>
                          {stats.open > 0 && (
                            <div className="text-[11px] text-error-500 dark:text-error-400">
                              {formatCurrency(stats.open)} offen
                            </div>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}

            {/* ---------------- Fußzeile ---------------- */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-5 py-3 dark:border-gray-700/70 dark:bg-gray-800/40">
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                {viewMode === 'events'
                  ? `${eventRows.length} von ${visibleEvents.length} Events · ${formatCurrency(stats.total)} gesamt`
                  : `${memberRows.length} von ${filteredMembers.length} Mitgliedern · ${formatCurrency(stats.total)} gesamt`}
              </p>
              <button
                type="button"
                onClick={() => setShowExportModal(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />
                Liste exportieren
              </button>
            </div>
          </div>
        </>
      )}
      {/* New Event Modal */}
      <ModalShell
        open={showNewEventModal}
        onClose={() => {
          setShowNewEventModal(false);
          setNewEventName('');
        }}
        variant="form"
        size="sm"
        icon={CalendarIcon}
        title="Neues Event erstellen"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{
              label: 'Abbrechen',
              onClick: () => {
                setShowNewEventModal(false);
                setNewEventName('');
              }
            }}
            primary={{
              label: 'Erstellen',
              onClick: handleCreateEvent,
              disabled: !newEventName.trim() || !newEventMonth
            }}
          />
        }
      >
        <ModalSection>
          <div className="space-y-4">
            <AttributeLabel iconKey="eventTitle" htmlFor="new-drink-event-name" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
              Event-Name
            </AttributeLabel>
            <input
              type="text"
              id="new-drink-event-name"
              value={newEventName}
              onChange={(e) => setNewEventName(e.target.value)}
              placeholder="z.B. Sommerfest 2024"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
            <div>
              <AttributeLabel iconKey="date" htmlFor="new-drink-event-month" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Abrechnungsmonat
              </AttributeLabel>
              <input
                type="month"
                id="new-drink-event-month"
                value={newEventMonth}
                onChange={(e) => setNewEventMonth(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Monat und Jahr werden in der Tabelle angezeigt.</p>
            </div>
          </div>
        </ModalSection>
      </ModalShell>

      {/* Event-Beleg anzeigen und verknüpfen */}
      {documentEvent && (
        <ModalShell
          open={!!documentEvent}
          onClose={() => setDocumentEvent(null)}
          variant="choice"
          size="md"
          icon={PaperClipIcon}
          title={documentEvent.beschriftung}
          subtitle={formatEventMonth(documentEvent)}
        >
          {documentEvent.document_id ? (
            <div className="mb-5 rounded-2xl border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
              <p className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">Aktuell angehängt</p>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-bold text-gray-900 dark:text-white">{documentEvent.document_name || 'Dokument'}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {documentEvent.document_path && (
                    <button
                      onClick={() => setPreviewDocument({ id: documentEvent.document_id!, name: documentEvent.document_name || 'Dokument', file_path: documentEvent.document_path! })}
                      className="flex h-10 items-center gap-2 rounded-xl bg-white px-3 text-sm font-bold text-primary-700 shadow-sm hover:bg-primary-100 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-gray-700"
                    >
                      <EyeIcon className="h-4 w-4" /> Anzeigen
                    </button>
                  )}
                  <button onClick={() => attachDocumentToEvent(null)} disabled={documentSaving} className="h-10 rounded-xl px-3 text-sm font-bold text-error-600 hover:bg-error-100 disabled:opacity-50 dark:text-error-400 dark:hover:bg-error-900/30">
                    Entfernen
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-5 rounded-xl border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">Für dieses Event ist noch kein Beleg angehängt.</div>
          )}

          <AttributeLabel iconKey="document" htmlFor="event-document-search" className="mb-2" textClassName="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Dokument auswählen</AttributeLabel>
          <div className="relative mb-3">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input id="event-document-search" type="search" value={documentSearch} onChange={(e) => setDocumentSearch(e.target.value)} placeholder="Dokumentname durchsuchen …" className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-4 text-sm text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </div>

          {documentsLoading ? (
            <div className="flex items-center justify-center py-12"><div className="h-7 w-7 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" /></div>
          ) : (
            <div className="space-y-2">
              {availableDocuments
                .filter(document => document.name.toLowerCase().includes(documentSearch.toLowerCase()))
                .map(document => {
                  const isAttached = document.id === documentEvent.document_id;
                  return (
                    <div key={document.id} className={`flex items-center gap-3 rounded-xl border p-3 ${isAttached ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"><DocumentTextIcon className="h-5 w-5" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{document.name}</p>
                        {document.created_at && <p className="text-[11px] text-gray-500 dark:text-gray-400">{new Date(document.created_at).toLocaleDateString('de-DE')}</p>}
                      </div>
                      <button onClick={() => setPreviewDocument(document)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-700" title="Dokument anzeigen"><EyeIcon className="h-4 w-4" /></button>
                      <button onClick={() => attachDocumentToEvent(document)} disabled={isAttached || documentSaving} className="flex h-9 items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-xs font-bold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50">
                        {isAttached ? <CheckCircleIcon className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                        {isAttached ? 'Verknüpft' : 'Anhängen'}
                      </button>
                    </div>
                  );
                })}
              {availableDocuments.filter(document => document.name.toLowerCase().includes(documentSearch.toLowerCase())).length === 0 && (
                <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Keine passenden Dokumente gefunden.</div>
              )}
            </div>
          )}
        </ModalShell>
      )}

      {/* Quick Entry Modal */}
      {/* ---------------- Schnelleingabe: alle Mitglieder auf einmal ---------------- */}
      {showQuickEntryModal && quickEntryEventId && (
        <ModalShell
          open={showQuickEntryModal}
          onClose={() => setShowQuickEntryModal(false)}
          variant="form"
          size="xl"
          icon={CurrencyEuroIcon}
          title="Schnelleingabe"
          subtitle={
            (() => {
              const ev = events.find(e => e.id === quickEntryEventId);
              return ev ? `${ev.beschriftung} · ${formatEventMonth(ev)}` : 'Event wählen';
            })()
          }
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setShowQuickEntryModal(false) }}
              primary={{
                label:
                  quickEntrySum > 0
                    ? `Speichern (${formatCurrency(quickEntrySum)})`
                    : 'Speichern',
                onClick: handleQuickEntrySave,
                loading: qeSaving,
                disabled: quickEntryCount === 0,
              }}
            />
          }
        >
          <div className="space-y-5">
            {/* Event */}
            <ModalSection title="Event">
              <div className="flex flex-wrap gap-2">
                {visibleEvents.map(event => {
                  const on = event.id === quickEntryEventId;
                  return (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => setQuickEntryEventId(event.id)}
                      className={`flex flex-col items-start gap-0.5 rounded-lg border px-3.5 py-2 text-left transition-colors ${
                        on
                          ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                      }`}
                    >
                      <span className="text-[13px] font-bold">{event.beschriftung}</span>
                      <span className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        {formatEventMonth(event)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </ModalSection>

            {/* Zahlweise */}
            <ModalSection title="Zahlweise">
              <div className="flex flex-wrap gap-2">
                {([
                  { key: 'cash', label: 'Bar', icon: BanknotesIcon },
                  { key: 'paypal', label: 'PayPal', icon: CreditCardIcon },
                  { key: 'open', label: 'noch offen', icon: ExclamationCircleIcon },
                ] as { key: QuickEntryMethod; label: string; icon: typeof BanknotesIcon }[]).map(
                  option => {
                    const on = qeMethod === option.key;
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setQeMethod(option.key)}
                        aria-pressed={on}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-semibold transition-colors ${
                          on
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                        }`}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {option.label}
                      </button>
                    );
                  },
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {qeMethod === 'open'
                  ? 'Beträge werden als offen gespeichert — die Mitglieder erhalten eine Zahlungsaufforderung.'
                  : 'Beträge werden direkt als bezahlt markiert.'}
              </p>
            </ModalSection>

            {/* Beträge */}
            <ModalSection title="Beträge">
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                Enter oder ↓ springt zum nächsten Mitglied, ↑ zurück, „+" zählt 1,00 hoch.
              </p>
              <div className="max-h-[42vh] space-y-1.5 overflow-y-auto pr-1">
                {quickEntryMembers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Keine Mitglieder vorhanden.</p>
                ) : (
                  quickEntryMembers.map((member, index) => {
                    const raw = qeAmounts[member.mg_nr] || '';
                    const value = parseAmount(raw);
                    const existing = drinkEntries.find(
                      e => e.member_id === member.mg_nr && e.month === quickEntryEventId,
                    );
                    return (
                      <div
                        key={member.mg_nr}
                        className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-lg border p-2 sm:grid-cols-[minmax(0,1fr)_150px_120px_32px] sm:gap-3 ${
                          value > 0
                            ? 'border-primary-200 bg-primary-50/60 dark:border-primary-900/50 dark:bg-primary-900/10'
                            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]'
                        }`}
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <MemberAvatar
                            member={{ vorname: member.vorname, name: member.name }}
                            photoUrl={getMemberImageUrl(member) || undefined}
                            hasImageError={!!imageErrors[`member-${member.mg_nr}`]}
                            onImageError={() =>
                              setImageErrors(prev => ({ ...prev, [`member-${member.mg_nr}`]: true }))
                            }
                            size="sm"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                              {`${member.vorname} ${member.name}`.trim()}
                            </div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">
                              {existing
                                ? `bereits erfasst: ${formatCurrency(existing.amount)}`
                                : `Nr. ${member.mitglied_nr || '—'}`}
                            </div>
                          </div>
                        </div>

                        {/* Presets */}
                        <div className="hidden gap-1.5 sm:flex">
                          {[1, 2, 5].map(preset => (
                            <button
                              key={preset}
                              type="button"
                              tabIndex={-1}
                              onClick={() => setQuickEntryAmount(member.mg_nr, value + preset)}
                              className="h-8 flex-1 rounded-lg border border-gray-300 bg-white text-xs font-bold text-gray-700 transition-colors hover:border-primary-500 hover:text-primary-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                            >
                              +{preset}
                            </button>
                          ))}
                        </div>

                        <input
                          data-qe-input="true"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={raw}
                          aria-label={`Betrag für ${member.vorname} ${member.name}`}
                          onChange={e =>
                            setQeAmounts(prev => ({ ...prev, [member.mg_nr]: e.target.value }))
                          }
                          onFocus={e => e.currentTarget.select()}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === 'ArrowDown') {
                              e.preventDefault();
                              focusQuickEntryRow(index + 1);
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              focusQuickEntryRow(index - 1);
                            } else if (e.key === '+') {
                              e.preventDefault();
                              setQuickEntryAmount(member.mg_nr, value + 1);
                            } else if (e.key === 'Escape') {
                              setShowQuickEntryModal(false);
                            }
                          }}
                          className="h-8 w-full rounded-lg border border-gray-300 px-2.5 text-right text-[13px] font-semibold tabular-nums text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        />

                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => {
                            setQuickEntryAmount(member.mg_nr, 0);
                            focusQuickEntryRow(index);
                          }}
                          aria-label="Betrag leeren"
                          className="hidden h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-error-600 dark:hover:bg-gray-700 sm:flex"
                        >
                          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </ModalSection>

            <p className="border-t border-gray-100 pt-3 text-[13px] font-semibold text-gray-700 dark:border-gray-700/70 dark:text-gray-200">
              {quickEntryCount === 0
                ? 'Noch keine Beträge erfasst'
                : `${quickEntryCount} ${quickEntryCount === 1 ? 'Mitglied' : 'Mitglieder'} · Summe ${formatCurrency(quickEntrySum)}`}
            </p>
          </div>
        </ModalShell>
      )}

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm?.();
            setConfirmModal({ ...confirmModal, visible: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm?.();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />

      {/* Export Modal */}
      <DrinkListExport
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      {/* Help Modal */}
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {showHelpModal && (
        <ModalShell
          open={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          variant="detail"
          size="lg"
          icon={QuestionMarkCircleIcon}
          title="Hilfe - Verzehrkosten"
          footer={
            <ModalActions
              primary={{ label: 'Verstanden', onClick: () => setShowHelpModal(false) }}
            />
          }
        >
            <div className="space-y-4">
              <div className="flex gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    Event erstellen
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    Klicken Sie auf <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-800/30 text-primary-700 dark:text-primary-300 rounded text-xs font-medium">
                      <PlusIcon className="h-3 w-3" /> Neues Event
                    </span> und geben Sie einen aussagekräftigen Namen ein.
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                    <strong>Beispiele:</strong> "Sommerfest 2024", "Weihnachtsfeier", "Vereinsturnier"
                  </p>
                  <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700/30 rounded-md p-2 mt-2">
                    <p className="text-xs text-warning-800 dark:text-warning-300 flex items-start gap-1">
                      <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span><strong>Wichtig:</strong> Nur Events ohne Beiträge können wieder gelöscht werden.</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                    <CurrencyEuroIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    Beträge erfassen
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-primary-600 dark:text-primary-400">•</span>
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        <strong className="text-gray-900 dark:text-white">Doppelklick</strong> auf eine Tabellenzelle für direktes Eintragen einzelner Beträge
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary-600 dark:text-primary-400">•</span>
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        <strong className="text-gray-900 dark:text-white">Schnelleingabe</strong>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-800/30 text-primary-700 dark:text-primary-300 rounded text-xs font-medium ml-1">
                          <CurrencyEuroIcon className="h-3 w-3" /> Schnelleingabe
                        </span> für alle Mitglieder nacheinander
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    Zahlungen verwalten
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-3">
                    Markieren Sie bezahlte Beträge mit einem Klick als
                    <span className="inline-block px-2 py-0.5 bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-300 rounded text-xs font-medium mx-1">Bar</span>
                    oder
                    <span className="inline-block px-2 py-0.5 bg-info-100 dark:bg-info-800/30 text-info-700 dark:text-info-300 rounded text-xs font-medium mx-1">PayPal</span>
                  </p>
                  <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-700 rounded-lg p-3">
                    <p className="text-sm font-semibold text-info-900 dark:text-info-200 mb-1 flex items-center gap-2">
                      <CreditCardIcon className="h-4 w-4" />
                      PayPal IPN (Instant Payment Notification)
                    </p>
                    <p className="text-xs text-info-800 dark:text-info-300 leading-relaxed">
                      Bei aktiviertem PayPal IPN werden Zahlungen <strong>automatisch erfasst</strong>:
                    </p>
                    <ul className="text-xs text-info-700 dark:text-info-400 mt-1 space-y-0.5 ml-4">
                      <li>• Weiterleitung an: <code className="bg-white/50 dark:bg-gray-800/50 px-1 rounded">https://mein-hundesportverein.de/paypal</code></li>
                      <li>• Echtzeit-Zahlungsbestätigung vom PayPal-Server</li>
                      <li>• Automatische Zuordnung zu Mitglied und Event</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  4
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                    <DocumentArrowDownIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    Listen exportieren
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-2">
                    Laden Sie Getränkelisten mit allen offenen Beträgen herunter:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-error-100 dark:bg-error-800/30 text-error-700 dark:text-error-300 rounded text-xs font-medium">
                      <DocumentTextIcon className="h-3 w-3" />
                      PDF-Format
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-success-100 dark:bg-success-800/30 text-success-700 dark:text-success-300 rounded text-xs font-medium">
                      <TableCellsIcon className="h-3 w-3" />
                      Excel-Format
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Ideal zum Ausdrucken für Veranstaltungen oder zur Kassenabrechnung.
                    Nutzen Sie Filter für spezifische Auswertungen (nur offene Beträge, bestimmte Events, etc.).
                  </p>
                </div>
              </div>
            </div>
        </ModalShell>
      )}
      {previewDocument && (
        <DocumentViewer
          documentId={previewDocument.id}
          fileName={previewDocument.name}
          filePath={previewDocument.file_path}
          onClose={() => setPreviewDocument(null)}
        />
      )}
    </div>
  );
};

export default VerzehrkostenBeta;
